import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiClient } from "@/lib/api";
import { getBrand } from "@/lib/brand";

const ESTADOS_MX = [
  "Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas","Chihuahua",
  "Ciudad de México","Coahuila","Colima","Durango","Estado de México","Guanajuato","Guerrero",
  "Hidalgo","Jalisco","Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla","Querétaro",
  "Quintana Roo","San Luis Potosí","Sinaloa","Sonora","Tabasco","Tamaulipas","Tlaxcala",
  "Veracruz","Yucatán","Zacatecas",
];

const RELACIONES = ["Madre","Padre","Cónyuge","Hijo/a","Hermano/a","Abuelo/a","Otro"];

const beneficiarioVacio = () => ({
  nombre: "", relacion: "", telefono: "", telefono_alt: "",
  calle: "", numero_ext: "", numero_int: "",
  colonia: "", municipio: "", estado: "",
  codigo_postal: "", referencias: "",
});

const initialRegistro = () => ({
  nombre: "", email: "", telefono: "",
  direccion_eu_calle: "", direccion_eu_ciudad: "", direccion_eu_estado: "", direccion_eu_zip: "",
  plan: "",
  beneficiarios: [beneficiarioVacio()],
  terminos: false,
});

export default function Landing() {
  const brand = getBrand();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRegistro, setShowRegistro] = useState(false);
  const [registroForm, setRegistroForm] = useState(initialRegistro);
  const [loading, setLoading] = useState(false);
  const [contactoForm, setContactoForm] = useState({ nombre: "", email: "", mensaje: "" });
  const [contactoLoading, setContactoLoading] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  // Estado del add-on Consulta médica digital — por plan (true/false)
  const [healthScanByPlan, setHealthScanByPlan] = useState({});
  // Billing period: 'mensual' | 'anual'
  const [billing, setBilling] = useState("anual");

  // Fade-in observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in")),
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );
    document.querySelectorAll(".feature-card, .servicio-card, .plan-card, .testimonio-card")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const openRegistro = () => navigate("/cuenta/registro");

  const maxBeneficiarios = registroForm.plan === "estandar" ? 4 : 1;

  const setTitularField = (field, value) =>
    setRegistroForm((f) => ({ ...f, [field]: value }));

  const setBeneficiarioField = (idx, field, value) =>
    setRegistroForm((f) => {
      const arr = [...f.beneficiarios];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...f, beneficiarios: arr };
    });

  const handlePlanChange = (newPlan) => {
    const max = newPlan === "estandar" ? 4 : 1;
    setRegistroForm((f) => ({
      ...f,
      plan: newPlan,
      beneficiarios: f.beneficiarios.slice(0, max).length
        ? f.beneficiarios.slice(0, max)
        : [beneficiarioVacio()],
    }));
  };

  const agregarBeneficiario = () => {
    if (registroForm.beneficiarios.length >= maxBeneficiarios) return;
    setRegistroForm((f) => ({ ...f, beneficiarios: [...f.beneficiarios, beneficiarioVacio()] }));
  };

  const quitarBeneficiario = (idx) => {
    if (registroForm.beneficiarios.length <= 1) return;
    setRegistroForm((f) => ({
      ...f,
      beneficiarios: f.beneficiarios.filter((_, i) => i !== idx),
    }));
  };

  const handleRegistro = async (e) => {
    e.preventDefault();
    if (!registroForm.terminos) {
      toast.error("Debes aceptar los términos y condiciones");
      return;
    }
    if (!registroForm.plan) {
      toast.error("Selecciona un plan");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/registro", registroForm);
      toast.success("¡Registro exitoso! Te contactaremos para activar la membresía.");
      setShowRegistro(false);
      setRegistroForm(initialRegistro());
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error al registrarse. Intenta nuevamente.";
      toast.error(typeof detail === "string" ? detail : "Error al registrarse.");
    } finally {
      setLoading(false);
    }
  };

  const handleContacto = async (e) => {
    e.preventDefault();
    setContactoLoading(true);
    try {
      await apiClient.post("/contacto", contactoForm);
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setContactoForm({ nombre: "", email: "", mensaje: "" });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Intenta de nuevo.");
    } finally {
      setContactoLoading(false);
    }
  };

  const elegirPlan = async (plan) => {
    // Redirige al portal donde se completa registro + beneficiarios + pago
    const addon = healthScanByPlan[plan.id] ? "&addon=health_scan" : "";
    navigate(`/cuenta/registro?plan=${plan.id}&billing=${billing}${addon}`);
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <Helmet>
        <html lang={brand.locale} />
        <title>{brand.seo.title}</title>
        <meta name="description" content={brand.seo.description} />
        <meta property="og:title" content={brand.seo.title} />
        <meta property="og:description" content={brand.seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={brand.seo.canonical} />
        <meta property="og:locale" content="es_MX" />
        <link rel="canonical" href={brand.seo.canonical} />
      </Helmet>
      {/* NAVBAR */}
      <nav className="navbar" data-testid="navbar">
        <div className="container navbar-content">
          <Link to="/" className="logo" data-testid="brand-logo">
            <img src={brand.logo} alt={brand.name} style={{ height: 56, width: "auto" }} />
            <span className="brand-name">
              <strong>{brand.shortName}</strong>
              <small>{brand.tagline}</small>
            </span>
          </Link>
          <ul className={`nav-links ${menuOpen ? "open" : ""}`}>
            <li><a href="#inicio" onClick={closeMenu} data-testid="nav-inicio">Inicio</a></li>
            <li><a href="#servicios" onClick={closeMenu} data-testid="nav-servicios">Servicios</a></li>
            <li><a href="#planes" onClick={closeMenu} data-testid="nav-planes">Planes</a></li>
            <li><a href="#contacto" onClick={closeMenu} data-testid="nav-contacto">Contacto</a></li>
            <li><Link to="/cuenta/login" onClick={closeMenu} data-testid="nav-login" style={{ color: "var(--primary-color)", fontWeight: 600 }}>Iniciar sesión</Link></li>
          </ul>
          <button className="menu-toggle" onClick={() => setMenuOpen((o) => !o)} data-testid="menu-toggle" aria-label="Menú">
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section
        className={`hero hero-${brand.id}`}
        id="inicio"
        data-testid="hero-section"
        style={{
          background: brand.theme.heroBackground,
          color: brand.theme.heroTextColor,
        }}
      >
        <div className="hero-content">
          <h1 style={{ color: brand.theme.heroTextColor }}>{brand.hero.title}</h1>
          <p style={{ color: brand.theme.heroTextColor, opacity: 0.92 }}>{brand.hero.subtitle}</p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => openRegistro()} data-testid="hero-registro-btn">
              {brand.hero.primaryCta}
            </button>
            <a href={brand.whatsapp} className="btn btn-secondary" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
              <i className="fab fa-whatsapp" /> WhatsApp
            </a>
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-photo">
            <img
              src={brand.hero.photoUrl}
              alt={brand.hero.photoAlt}
              data-testid="hero-photo"
            />
            <div className="hero-photo-badge">
              <i className="fas fa-heart" />
              <div>
                <strong>{brand.hero.photoBadgeTitle}</strong>
                <span>{brand.hero.photoBadgeSub}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RAZÓN DE SER */}
      <section className="razon" data-testid="razon-section">
        <div className="container razon-container">
          <div className="razon-grid">
            <div className="razon-text">
              <span className="razon-badge">{brand.razon.badge}</span>
              <h2>{brand.razon.titleA}<br/><span className="razon-highlight">{brand.razon.titleHighlight}</span>{brand.razon.titleB}</h2>
              {brand.razon.paragraphs.map((p, idx) => (
                <p key={idx} className="razon-lead" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
              <div className="razon-stats">
                <div className="razon-stat">
                  <strong>24/7</strong>
                  <span>365 días al año</span>
                </div>
                <div className="razon-stat">
                  <strong>32</strong>
                  <span>estados de cobertura</span>
                </div>
                <div className="razon-stat">
                  <strong>15 min</strong>
                  <span>tiempo promedio de respuesta</span>
                </div>
              </div>
              <button className="btn btn-primary btn-large" onClick={() => openRegistro()} data-testid="razon-registro-btn">
                {brand.razon.ctaButton}
              </button>
            </div>
            <div className="razon-card-list">
              <div className="razon-photo-card">
                <img
                  src={brand.razon.photoUrl}
                  alt={brand.razon.photoAlt}
                  data-testid="razon-photo"
                />
                <div className="razon-photo-overlay">
                  <p>&ldquo;{brand.razon.photoQuote}&rdquo;</p>
                  <span>{brand.razon.photoAuthor}</span>
                </div>
              </div>
              {brand.razon.minis.map((m, idx) => (
                <div key={idx} className="razon-mini">
                  <i className={m.icon} />
                  <div><strong>{m.title}</strong><span>{m.sub}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features">
        <div className="container">
          <h2>¿Por qué elegir {brand.name}?</h2>
          <div className="features-grid">
            {brand.features.map((f, i) => (
              <div key={i} className="feature-card" data-testid={`feature-card-${i}`}>
                <i className={f.icon} />
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section className="servicios" id="servicios" data-testid="servicios-section">
        <div className="container">
          <h2>Nuestros Servicios</h2>
          <div className="servicios-grid">
            {brand.servicios.map((s, i) => (
              <div key={i} className="servicio-card" data-testid={`servicio-card-${i}`}>
                <div className="servicio-icon"><i className={s.icon} /></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANES */}
      <section className="planes" id="planes" data-testid="planes-section">
        <div className="container">
          <h2>Elige tu Plan</h2>

          <div className="billing-toggle-wrap">
            <div className="billing-toggle" role="tablist" data-testid="billing-toggle">
              <button
                type="button"
                className={billing === "mensual" ? "active" : ""}
                onClick={() => setBilling("mensual")}
                data-testid="billing-mensual"
              >
                Mensual
              </button>
              <button
                type="button"
                className={billing === "anual" ? "active" : ""}
                onClick={() => setBilling("anual")}
                data-testid="billing-anual"
              >
                Anual <span className="save-badge">-50%</span>
              </button>
            </div>
          </div>

          <div className="planes-grid">
            {brand.planes.map((p) => {
              const isAnual = billing === "anual" && p.precioAnualEquivalente;
              const shownPrice = isAnual ? p.precioAnualEquivalente : p.precio;
              const strikePrice = isAnual ? p.precio : null;
              return (
              <div key={p.id} className={`plan-card ${p.featured ? "featured" : ""}`} data-testid={`plan-card-${p.id}`}>
                {p.featured && <div className="plan-badge">MÁS POPULAR</div>}
                <div className="plan-header">
                  <h3>{p.nombre}</h3>
                  <p className="plan-desc">{p.desc}</p>
                </div>

                {isAnual && (
                  <div style={{ textAlign: "center", marginTop: 14 }}>
                    <span style={{
                      display: "inline-block",
                      background: "#E8F5D9",
                      color: "#5BA628",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      marginBottom: 6,
                    }}>
                      {p.promoBadge || "OFERTA ANUAL"}
                    </span>
                    {strikePrice && (
                      <div style={{ color: "#E84C5E", textDecoration: "line-through", fontWeight: 600, fontSize: 15 }}>
                        {brand.currencySymbol}{strikePrice} {brand.currency}/mes
                      </div>
                    )}
                  </div>
                )}

                <div className="plan-price">
                  <span className="currency">{brand.currencySymbol}</span>
                  <span className="amount">{shownPrice}</span>
                  <span className="period">/mes</span>
                </div>
                <p style={{ textAlign: "center", fontSize: 12, opacity: 0.85, marginTop: -8, marginBottom: 6, fontWeight: 600 }}>
                  Precio + IVA
                </p>
                {isAnual && p.promoNota && (
                  <p style={{ textAlign: "center", fontSize: 12, color: "#4CAF50", fontWeight: 700, marginTop: -10, marginBottom: 14 }}>
                    ✓ {p.promoNota}
                  </p>
                )}
                {!isAnual && (
                  <p style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", marginTop: -10, marginBottom: 14 }}>
                    Facturación mensual
                  </p>
                )}

                <ul className="plan-features">
                  {p.features.map((f, idx) => (
                    <li key={idx}>
                      <i className={f.ok ? "fas fa-check" : "fas fa-times"} />
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>

                {/* ADD-ON: Consulta médica digital */}
                {brand.healthScanAddOn && (
                  <label
                    htmlFor={`addon-health-${p.id}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: 12,
                      border: `1.5px dashed ${healthScanByPlan[p.id] ? "#4CAF50" : "#cbd5e1"}`,
                      borderRadius: 10,
                      background: healthScanByPlan[p.id] ? "#f0fdf4" : "#f8fafc",
                      marginBottom: 14,
                      cursor: "pointer",
                      transition: "all .2s",
                    }}
                    data-testid={`addon-health-label-${p.id}`}
                  >
                    <input
                      id={`addon-health-${p.id}`}
                      type="checkbox"
                      checked={!!healthScanByPlan[p.id]}
                      onChange={(e) => setHealthScanByPlan((prev) => ({ ...prev, [p.id]: e.target.checked }))}
                      style={{ marginTop: 3, width: 18, height: 18, accentColor: "#4CAF50", cursor: "pointer" }}
                      data-testid={`addon-health-checkbox-${p.id}`}
                    />
                    <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4, color: "#0f172a" }}>
                      <div style={{ fontWeight: 700, marginBottom: 2 }}>
                        <i className="fas fa-heart-pulse" style={{ color: "#E84C5E", marginRight: 6 }} />
                        {brand.healthScanAddOn.name} +{brand.currencySymbol}{brand.healthScanAddOn.pricePerMonth} {brand.currency}/mes
                      </div>
                      <div style={{ color: "#475569", fontSize: 12 }}>
                        {brand.healthScanAddOn.description}
                      </div>
                      <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>
                        Escaneos extra: {brand.currencySymbol}{brand.healthScanAddOn.extraScanPrice} {brand.healthScanAddOn.currency} c/u (cobrados al momento)
                      </div>
                    </div>
                  </label>
                )}

                <button
                  className="btn btn-primary plan-btn"
                  onClick={() => elegirPlan(p)}
                  disabled={payingPlan === p.id}
                  data-testid={`plan-elegir-${p.id}-btn`}
                >
                  {payingPlan === p.id ? "Procesando..." : "Elegir Plan"}
                </button>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="testimonios">
        <div className="container">
          <h2>Lo que dicen nuestros clientes</h2>
          <div className="testimonios-grid">
            {brand.testimonios.map((t, i) => (
              <div key={i} className="testimonio-card" data-testid={`testimonio-card-${i}`}>
                <div className="stars">
                  {Array.from({ length: 5 }).map((_, idx) => <i key={idx} className="fas fa-star" />)}
                </div>
                <p>&ldquo;{t.texto}&rdquo;</p>
                <div className="testimonio-autor">
                  <strong>{t.nombre}</strong>
                  <span>{t.ciudad}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <h2>¿Listo para comenzar?</h2>
          <p>Únete a cientos de familias que confían en nosotros</p>
          <button className="btn btn-primary btn-large" onClick={() => openRegistro()} data-testid="cta-registro-btn">
            Registrarse Ahora
          </button>
        </div>
      </section>

      {/* CONTACTO */}
      <section className="contacto" id="contacto" data-testid="contacto-section">
        <div className="container">
          <h2>Contacto</h2>
          <div className="contacto-grid">
            <div className="contacto-info">
              <div className="info-item">
                <i className="fas fa-phone" />
                <div><h3>Teléfono</h3><p>{brand.contactPhone}</p></div>
              </div>
              <div className="info-item">
                <i className="fas fa-envelope" />
                <div><h3>Email</h3><p>{brand.contactEmail}</p></div>
              </div>
              <div className="info-item">
                <i className="fas fa-map-marker-alt" />
                <div><h3>Ubicación</h3><p>{brand.contactCity}</p></div>
              </div>
              <div className="info-item">
                <i className="fas fa-clock" />
                <div><h3>Horario</h3><p>24/7 - Atención Disponible</p></div>
              </div>
            </div>
            <form className="contacto-form" onSubmit={handleContacto} data-testid="contacto-form">
              <div className="form-group">
                <input type="text" placeholder="Tu nombre" required value={contactoForm.nombre}
                  onChange={(e) => setContactoForm((f) => ({ ...f, nombre: e.target.value }))}
                  data-testid="contacto-nombre" />
              </div>
              <div className="form-group">
                <input type="email" placeholder="Tu email" required value={contactoForm.email}
                  onChange={(e) => setContactoForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="contacto-email" />
              </div>
              <div className="form-group">
                <textarea placeholder="Tu mensaje" rows={5} required value={contactoForm.mensaje}
                  onChange={(e) => setContactoForm((f) => ({ ...f, mensaje: e.target.value }))}
                  data-testid="contacto-mensaje" />
              </div>
              <button type="submit" className="btn btn-primary" disabled={contactoLoading} data-testid="contacto-submit">
                {contactoLoading ? "Enviando..." : "Enviar Mensaje"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-section">
              <h4>{brand.name}</h4>
              <p>{brand.id === "mx" ? "Tu Ángel cuidándote a ti y a tu familia. Una llamada y llegamos." : "Tu Ángel siempre cerca de los tuyos en México, mientras tú estás lejos."}</p>
            </div>
            <div className="footer-section">
              <h4>Enlaces Rápidos</h4>
              <ul>
                <li><a href="#inicio">Inicio</a></li>
                <li><a href="#servicios">Servicios</a></li>
                <li><a href="#planes">Planes</a></li>
                <li><a href="#contacto">Contacto</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Síguenos</h4>
              <div className="social-links">
                <a href="#" aria-label="Facebook"><i className="fab fa-facebook" /></a>
                <a href="#" aria-label="Instagram"><i className="fab fa-instagram" /></a>
                <a href={brand.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"><i className="fab fa-whatsapp" /></a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 {brand.name}. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* MODAL REGISTRO */}
      <div className={`modal ${showRegistro ? "active" : ""}`} onClick={(e) => e.target === e.currentTarget && setShowRegistro(false)}>
        <div className="modal-content modal-content-wide">
          <button className="modal-close" onClick={() => setShowRegistro(false)} data-testid="modal-close">&times;</button>
          <h2>Registra tu Membresía</h2>
          <p className="modal-sub">Necesitamos los datos del titular (en EU) y los datos de tus familiares en México a quienes vamos a asistir.</p>
          <form className="registro-form" onSubmit={handleRegistro} data-testid="registro-form">

            {/* PLAN */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="fas fa-tag" /> Plan</h3>
              <div className="form-group">
                <label>Plan Seleccionado *</label>
                <select required value={registroForm.plan}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  data-testid="registro-plan">
                  <option value="">Selecciona un plan</option>
                  {brand.planes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} — {brand.currencySymbol}{p.precio} {brand.currency}{p.periodo || "/mes"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* TITULAR EU */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="fas fa-user-tie" /> Titular (Estados Unidos)</h3>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Nombre Completo *</label>
                  <input type="text" required value={registroForm.nombre}
                    onChange={(e) => setTitularField("nombre", e.target.value)}
                    data-testid="registro-nombre" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" required value={registroForm.email}
                    onChange={(e) => setTitularField("email", e.target.value)}
                    data-testid="registro-email" />
                </div>
                <div className="form-group">
                  <label>Teléfono EU *</label>
                  <input type="tel" required placeholder="+1 (XXX) XXX-XXXX" value={registroForm.telefono}
                    onChange={(e) => setTitularField("telefono", e.target.value)}
                    data-testid="registro-telefono" />
                </div>
                <div className="form-group">
                  <label>Dirección — Calle y número *</label>
                  <input type="text" required placeholder="123 Main St" value={registroForm.direccion_eu_calle}
                    onChange={(e) => setTitularField("direccion_eu_calle", e.target.value)}
                    data-testid="registro-eu-calle" />
                </div>
                <div className="form-group">
                  <label>Ciudad *</label>
                  <input type="text" required value={registroForm.direccion_eu_ciudad}
                    onChange={(e) => setTitularField("direccion_eu_ciudad", e.target.value)}
                    data-testid="registro-eu-ciudad" />
                </div>
                <div className="form-group">
                  <label>Estado (EU) *</label>
                  <input type="text" required placeholder="CA, TX, NY..." value={registroForm.direccion_eu_estado}
                    onChange={(e) => setTitularField("direccion_eu_estado", e.target.value)}
                    data-testid="registro-eu-estado" />
                </div>
                <div className="form-group">
                  <label>ZIP Code *</label>
                  <input type="text" required value={registroForm.direccion_eu_zip}
                    onChange={(e) => setTitularField("direccion_eu_zip", e.target.value)}
                    data-testid="registro-eu-zip" />
                </div>
              </div>
            </div>

            {/* BENEFICIARIOS MX */}
            <div className="form-section">
              <h3 className="form-section-title">
                <i className="fas fa-users" /> Beneficiarios en México
                <span className="form-section-counter">
                  {registroForm.beneficiarios.length}/{maxBeneficiarios}
                </span>
              </h3>
              <p className="form-section-help">Estas son las personas a las que enviaremos la servicio. Datos críticos: nombre, teléfono y dirección completa.</p>

              {registroForm.beneficiarios.map((b, idx) => (
                <div key={idx} className="beneficiario-card" data-testid={`beneficiario-${idx}`}>
                  <div className="beneficiario-header">
                    <strong>Beneficiario {idx + 1}</strong>
                    {registroForm.beneficiarios.length > 1 && (
                      <button type="button" onClick={() => quitarBeneficiario(idx)} className="link-danger" data-testid={`quitar-beneficiario-${idx}`}>
                        <i className="fas fa-trash" /> Quitar
                      </button>
                    )}
                  </div>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label>Nombre Completo *</label>
                      <input type="text" required value={b.nombre}
                        onChange={(e) => setBeneficiarioField(idx, "nombre", e.target.value)}
                        data-testid={`benef-${idx}-nombre`} />
                    </div>
                    <div className="form-group">
                      <label>Relación / Parentesco *</label>
                      <select required value={b.relacion}
                        onChange={(e) => setBeneficiarioField(idx, "relacion", e.target.value)}
                        data-testid={`benef-${idx}-relacion`}>
                        <option value="">Selecciona</option>
                        {RELACIONES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Teléfono México *</label>
                      <input type="tel" required placeholder="+52 ..." value={b.telefono}
                        onChange={(e) => setBeneficiarioField(idx, "telefono", e.target.value)}
                        data-testid={`benef-${idx}-tel`} />
                    </div>
                    <div className="form-group">
                      <label>Teléfono adicional</label>
                      <input type="tel" placeholder="Opcional" value={b.telefono_alt}
                        onChange={(e) => setBeneficiarioField(idx, "telefono_alt", e.target.value)}
                        data-testid={`benef-${idx}-telalt`} />
                    </div>
                  </div>
                  <h4 className="form-subsection">Dirección en México</h4>
                  <div className="form-grid-2">
                    <div className="form-group full-width">
                      <label>Calle *</label>
                      <input type="text" required value={b.calle}
                        onChange={(e) => setBeneficiarioField(idx, "calle", e.target.value)}
                        data-testid={`benef-${idx}-calle`} />
                    </div>
                    <div className="form-group">
                      <label>Núm. Exterior *</label>
                      <input type="text" required value={b.numero_ext}
                        onChange={(e) => setBeneficiarioField(idx, "numero_ext", e.target.value)}
                        data-testid={`benef-${idx}-numext`} />
                    </div>
                    <div className="form-group">
                      <label>Núm. Interior</label>
                      <input type="text" placeholder="Opcional" value={b.numero_int}
                        onChange={(e) => setBeneficiarioField(idx, "numero_int", e.target.value)}
                        data-testid={`benef-${idx}-numint`} />
                    </div>
                    <div className="form-group">
                      <label>Colonia *</label>
                      <input type="text" required value={b.colonia}
                        onChange={(e) => setBeneficiarioField(idx, "colonia", e.target.value)}
                        data-testid={`benef-${idx}-colonia`} />
                    </div>
                    <div className="form-group">
                      <label>Municipio / Alcaldía *</label>
                      <input type="text" required value={b.municipio}
                        onChange={(e) => setBeneficiarioField(idx, "municipio", e.target.value)}
                        data-testid={`benef-${idx}-municipio`} />
                    </div>
                    <div className="form-group">
                      <label>Estado *</label>
                      <select required value={b.estado}
                        onChange={(e) => setBeneficiarioField(idx, "estado", e.target.value)}
                        data-testid={`benef-${idx}-estado`}>
                        <option value="">Selecciona</option>
                        {ESTADOS_MX.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Código Postal *</label>
                      <input type="text" required maxLength={5} value={b.codigo_postal}
                        onChange={(e) => setBeneficiarioField(idx, "codigo_postal", e.target.value)}
                        data-testid={`benef-${idx}-cp`} />
                    </div>
                    <div className="form-group full-width">
                      <label>Referencias / Entre calles</label>
                      <input type="text" placeholder="Ej. casa color azul, frente al parque" value={b.referencias}
                        onChange={(e) => setBeneficiarioField(idx, "referencias", e.target.value)}
                        data-testid={`benef-${idx}-ref`} />
                    </div>
                  </div>
                </div>
              ))}

              {registroForm.beneficiarios.length < maxBeneficiarios && (
                <button type="button" className="btn btn-secondary btn-add" onClick={agregarBeneficiario} data-testid="agregar-beneficiario-btn">
                  <i className="fas fa-plus" /> Agregar otro beneficiario
                </button>
              )}
            </div>

            <div className="form-group checkbox">
              <input type="checkbox" required checked={registroForm.terminos}
                onChange={(e) => setTitularField("terminos", e.target.checked)}
                data-testid="registro-terminos" />
              <label>Acepto los términos y condiciones</label>
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading} data-testid="registro-submit">
              {loading ? "Registrando..." : "Registrar y continuar al pago"}
            </button>
            <p className="form-info">Los datos en México son críticos para poder enviar grúa, ambulancia o servicio legal.</p>
          </form>
        </div>
      </div>

      {/* WHATSAPP */}
      <a href={brand.whatsapp} className="whatsapp-flotante" target="_blank" rel="noopener noreferrer" title="Contáctanos por WhatsApp" data-testid="whatsapp-floating">
        <i className="fab fa-whatsapp" />
      </a>
    </>
  );
}
